import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { trashAPI } from "../services/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import "./AccountSettings.css";

function AccountSettings() {
  const { user, token, updateUser } = useAuth();
  const navigate = useNavigate();
  const [currentSubscriptionPlan, setCurrentSubscriptionPlan] = useState(
    user?.subscriptionPlan,
  );
  const [formData, setFormData] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
      }));
    }
  }, [user]);

  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteAcknowledged, setDeleteAcknowledged] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletedItems, setDeletedItems] = useState({
    teams: [],
    graphics: [],
    players: [],
    bragCards: [],
  });
  const [restoring, setRestoring] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch deleted items
        const deletedResponse = await trashAPI.getRecentlyDeleted();
        setDeletedItems({
          teams: deletedResponse.data.teams,
          graphics: deletedResponse.data.graphics,
          players: deletedResponse.data.players || [],
          bragCards: deletedResponse.data.bragCards || [],
        });

        // Fetch current subscription plan from API (fresher than JWT token)
        const membershipResponse = await fetch(
          `${
            process.env.REACT_APP_API_URL || "http://192.168.68.67:5000"
          }/api/auth/membership`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const membershipData = await membershipResponse.json();
        setCurrentSubscriptionPlan(membershipData.subscriptionPlan);
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };

    fetchData();
  }, [token]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setMessage({ type: "", text: "" }); // Clear message on change
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      // Validate passwords if changing
      if (showPasswordSection) {
        if (!formData.currentPassword) {
          setMessage({
            type: "error",
            text: "Current password is required to change password",
          });
          setSaving(false);
          return;
        }
        if (formData.newPassword !== formData.confirmPassword) {
          setMessage({ type: "error", text: "New passwords do not match" });
          setSaving(false);
          return;
        }
        if (formData.newPassword && formData.newPassword.length < 6) {
          setMessage({
            type: "error",
            text: "New password must be at least 6 characters",
          });
          setSaving(false);
          return;
        }
      }

      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL || "http://localhost:5000"
        }/api/auth/update-profile`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            currentPassword: showPasswordSection
              ? formData.currentPassword
              : undefined,
            newPassword:
              showPasswordSection && formData.newPassword
                ? formData.newPassword
                : undefined,
          }),
        },
      );

      const data = await response.json();

      if (response.ok) {
        updateUser({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
        });

        setMessage({ type: "success", text: "Profile updated successfully!" });
        setTimeout(() => setMessage({ type: "", text: "" }), 3000);

        // Clear password fields
        setFormData({
          ...formData,
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        setShowPasswordSection(false);
      } else {
        setMessage({
          type: "error",
          text: data.error || data.message || "Failed to update profile",
        });
        setTimeout(() => setMessage({ type: "", text: "" }), 3000);
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      // Show backend error message if available
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "An error occurred. Please try again.";
      setMessage({
        type: "error",
        text: errorMessage,
      });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreTeam = async (teamId) => {
    setRestoring(`team-${teamId}`);
    try {
      await trashAPI.restoreTeam(teamId);
      // Refresh deleted items
      const response = await trashAPI.getRecentlyDeleted();
      setDeletedItems({
        teams: response.data.teams,
        graphics: response.data.graphics,
        players: response.data.players || [],
        bragCards: response.data.bragCards || [],
      });
      setMessage({ type: "success", text: "Team restored successfully!" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
      setRestoring(null);
    } catch (err) {
      setMessage({ type: "error", text: "Failed to restore team" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
      setRestoring(null);
    }
  };

  const handleRestoreGraphic = async (graphicId) => {
    setRestoring(`graphic-${graphicId}`);
    try {
      await trashAPI.restoreGraphic(graphicId);
      // Refresh deleted items
      const response = await trashAPI.getRecentlyDeleted();
      setDeletedItems({
        teams: response.data.teams,
        graphics: response.data.graphics,
        players: response.data.players || [],
        bragCards: response.data.bragCards || [],
      });
      setMessage({ type: "success", text: "Graphic restored successfully!" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
      setRestoring(null);
    } catch (err) {
      setMessage({ type: "error", text: "Failed to restore graphic" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
      setRestoring(null);
    }
  };

  const handleRestorePlayer = async (playerId) => {
    setRestoring(`player-${playerId}`);
    try {
      await trashAPI.restorePlayer(playerId);
      const response = await trashAPI.getRecentlyDeleted();
      setDeletedItems({
        teams: response.data.teams,
        graphics: response.data.graphics,
        players: response.data.players || [],
        bragCards: response.data.bragCards || [],
      });
      setMessage({ type: "success", text: "Player restored successfully!" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
      setRestoring(null);
    } catch (err) {
      setMessage({ type: "error", text: "Failed to restore player" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
      setRestoring(null);
    }
  };

  const handleRestoreBragCard = async (cardId) => {
    setRestoring(`bragcard-${cardId}`);
    try {
      await trashAPI.restoreBragCard(cardId);
      const response = await trashAPI.getRecentlyDeleted();
      setDeletedItems({
        teams: response.data.teams,
        graphics: response.data.graphics,
        players: response.data.players || [],
        bragCards: response.data.bragCards || [],
      });
      setMessage({ type: "success", text: "BragCard restored successfully!" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
      setRestoring(null);
    } catch (err) {
      setMessage({ type: "error", text: "Failed to restore BragCard" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
      setRestoring(null);
    }
  };

  async function handleDeleteAccount() {
    if (deleteConfirmText !== "DELETE" || !deleteAcknowledged) return;
    setDeleting(true);
    try {
      await api.delete("/auth/account");
      localStorage.clear();
      window.location.href = "/login";
    } catch (err) {
      console.error("Delete account failed:", err);
      setMessage({
        text: "Something went wrong. Please contact support.",
        type: "error",
      });
      setDeleting(false);
      setShowDeleteModal(false);
    }
  }

  return (
    <>
      <Navbar />
      <div className="account-settings-container">
        <div className="account-settings-content">
          <button className="back-button" onClick={() => navigate("/")}>
            ← Back to Home
          </button>

          <div className="settings-header">
            <h1>Account Settings</h1>
            <p>Manage your account information and preferences</p>
          </div>

          <form onSubmit={handleSubmit} className="settings-form">
            {/* Role Section */}
            <div className="form-section">
              <h2 className="form-section-title">My Role</h2>
              <p
                style={{
                  fontSize: "14px",
                  color: "#4a5568",
                  marginBottom: "16px",
                }}
              >
                This personalizes your Playmaker experience — what you see on
                your home screen and in the menu.
              </p>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  marginBottom: "16px",
                }}
              >
                {[
                  {
                    value: "coach",
                    emoji: "📋",
                    label: "Coach",
                    desc: "Team graphics and roster management",
                  },
                  {
                    value: "parent",
                    emoji: "⭐",
                    label: "Parent",
                    desc: "Player analytics and brag cards",
                  },
                  {
                    value: "both",
                    emoji: "🏆",
                    label: "Both",
                    desc: "Everything — coach tools and player analytics",
                  },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={async () => {
                      try {
                        const res = await api.put("/auth/update-role", {
                          role: opt.value,
                        });
                        if (res.data.role) updateUser({ role: res.data.role });
                      } catch (err) {
                        console.error("Role update failed:", err);
                      }
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "14px",
                      padding: "14px 16px",
                      borderRadius: "10px",
                      cursor: "pointer",
                      border: `2px solid ${user?.role === opt.value ? "#c0001a" : "#e2e6ef"}`,
                      background:
                        user?.role === opt.value ? "#fff5f5" : "#f8f9fc",
                      textAlign: "left",
                      fontFamily: "Montserrat, sans-serif",
                      transition: "all 0.15s",
                    }}
                  >
                    <span style={{ fontSize: "24px" }}>{opt.emoji}</span>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontWeight: 800,
                          color: "#1a1e2e",
                          fontSize: "15px",
                        }}
                      >
                        {opt.label}
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#4a5568",
                          marginTop: "2px",
                        }}
                      >
                        {opt.desc}
                      </div>
                    </div>
                    {user?.role === opt.value && (
                      <span
                        style={{
                          color: "#c0001a",
                          fontWeight: 800,
                          fontSize: "16px",
                        }}
                      >
                        ✓
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Personal Information Section */}
            <div className="form-section">
              <h2 className="form-section-title">Personal Information</h2>

              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>User ID</label>
                <input
                  type="text"
                  value={user?.id || ""}
                  readOnly
                  disabled
                  style={{ backgroundColor: "#f7fafc", cursor: "not-allowed" }}
                />
                <span className="form-helper-text">
                  Your unique user identifier (read-only)
                </span>
              </div>

              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  readOnly
                  disabled
                  style={{ backgroundColor: "#f7fafc", cursor: "not-allowed" }}
                />
                <span className="form-helper-text">
                  Email cannot be changed after registration. Need to change it?{" "}
                  <a
                    href="/contact"
                    style={{ color: "#48bb78", textDecoration: "underline" }}
                  >
                    Contact us
                  </a>
                </span>
              </div>
            </div>

            {/* Password Section */}
            <div className="form-section">
              <div className="section-header-with-toggle">
                <h2 className="form-section-title">Password</h2>
                {!showPasswordSection && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{ width: "auto", flexShrink: 0 }}
                    onClick={() => setShowPasswordSection(true)}
                  >
                    Change Password
                  </button>
                )}
              </div>

              {showPasswordSection && (
                <>
                  <div className="form-group">
                    <label>Current Password *</label>
                    <input
                      type="password"
                      name="currentPassword"
                      value={formData.currentPassword}
                      onChange={handleChange}
                      placeholder="Enter your current password"
                    />
                  </div>

                  <div className="form-group">
                    <label>New Password *</label>
                    <input
                      type="password"
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleChange}
                      placeholder="Enter new password (min 6 characters)"
                    />
                  </div>

                  <div className="form-group">
                    <label>Confirm New Password *</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm new password"
                    />
                  </div>

                  <button
                    type="button"
                    className="btn-cancel-password"
                    onClick={() => {
                      setShowPasswordSection(false);
                      setFormData({
                        ...formData,
                        currentPassword: "",
                        newPassword: "",
                        confirmPassword: "",
                      });
                    }}
                  >
                    Cancel Password Change
                  </button>
                </>
              )}
            </div>

            {/* Recently Deleted Section */}
            <div className="form-section">
              <h2 className="form-section-title">Recently Deleted</h2>
              <div className="recently-deleted-info">
                <p className="info-description">
                  Items you delete are stored for 3 days before being
                  permanently removed. Click the restore button to bring them
                  back.
                </p>

                {deletedItems.teams.length +
                  deletedItems.graphics.length +
                  (deletedItems.players?.length || 0) +
                  (deletedItems.bragCards?.length || 0) >
                0 ? (
                  <div className="deleted-summary">
                    {/* Deleted Teams List */}
                    {deletedItems.teams.length > 0 && (
                      <div className="deleted-items-preview">
                        <h4 className="preview-heading">
                          ⚾🥎 Teams ({deletedItems.teams.length})
                        </h4>
                        <ul className="deleted-items-list">
                          {deletedItems.teams.map((team) => {
                            const isOrgTeam = team.organizationTeam === true;
                            const isLockedOrgTeam =
                              isOrgTeam &&
                              team.deletedAt &&
                              currentSubscriptionPlan !== "organization";
                            return (
                              <li
                                key={team.id}
                                className={`deleted-item ${
                                  isLockedOrgTeam ? "org-item-deleted" : ""
                                }`}
                              >
                                <div className="item-info">
                                  <span className="item-name">{team.name}</span>
                                  <span className="item-meta">
                                    {team.ageGroup} • {team.season}
                                  </span>
                                  {isLockedOrgTeam && (
                                    <span className="renewal-badge-inline">
                                      ⚠️ Renewal Required
                                    </span>
                                  )}
                                </div>
                                {isLockedOrgTeam ? (
                                  <button
                                    className="btn-restore-inline-disabled"
                                    disabled
                                    title="Renew your Organization subscription to restore"
                                  >
                                    🔒 Renew to Restore
                                  </button>
                                ) : (
                                  <button
                                    className="btn-restore-inline"
                                    onClick={() => handleRestoreTeam(team.id)}
                                    disabled={restoring === `team-${team.id}`}
                                  >
                                    {restoring === `team-${team.id}`
                                      ? "Restoring..."
                                      : "↶ Restore"}
                                  </button>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    {/* Deleted Graphics List */}
                    {deletedItems.graphics.length > 0 && (
                      <div className="deleted-items-preview">
                        <h4 className="preview-heading">
                          Graphics ({deletedItems.graphics.length})
                        </h4>
                        <ul className="deleted-items-list">
                          {deletedItems.graphics.map((graphic) => {
                            // Org graphics are never locked - they can be restored by anyone with access
                            const isLockedOrgGraphic = false;
                            return (
                              <li
                                key={graphic.id}
                                className={`deleted-item ${
                                  isLockedOrgGraphic ? "org-item-deleted" : ""
                                }`}
                              >
                                <div className="item-info">
                                  <span className="item-name">
                                    {graphic.templateType === "tryout"
                                      ? `${graphic.graphicData.homeTeam} Tryouts`
                                      : graphic.templateType === "highlight"
                                        ? graphic.graphicData.highlightPlayer
                                        : `${graphic.graphicData.homeTeam} vs ${graphic.graphicData.awayTeam}`}
                                  </span>
                                  <span className="item-meta">
                                    {graphic.Team.name}
                                  </span>
                                  {isLockedOrgGraphic && (
                                    <span className="renewal-badge-inline">
                                      ⚠️ Renewal Required
                                    </span>
                                  )}
                                </div>
                                {isLockedOrgGraphic ? (
                                  <button
                                    className="btn-restore-inline-disabled"
                                    disabled
                                    title="Renew your Organization subscription to restore"
                                  >
                                    🔒 Renew to Restore
                                  </button>
                                ) : (
                                  <button
                                    className="btn-restore-inline"
                                    onClick={() =>
                                      handleRestoreGraphic(graphic.id)
                                    }
                                    disabled={
                                      restoring === `graphic-${graphic.id}`
                                    }
                                  >
                                    {restoring === `graphic-${graphic.id}`
                                      ? "Restoring..."
                                      : "↶ Restore"}
                                  </button>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    {/* Deleted Players List */}
                    {(deletedItems.players?.length || 0) > 0 && (
                      <div className="deleted-items-preview">
                        <h4 className="preview-heading">
                          Players ({deletedItems.players.length})
                        </h4>
                        <ul className="deleted-items-list">
                          {deletedItems.players.map((player) => (
                            <li key={player.id} className="deleted-item">
                              <div className="item-info">
                                <span className="item-name">
                                  {player.firstName} {player.lastName}
                                </span>
                                <span className="item-meta">
                                  {player.sport}
                                  {player.playingLevel
                                    ? ` · ${player.playingLevel}`
                                    : ""}
                                </span>
                              </div>
                              <button
                                className="btn-restore-inline"
                                onClick={() => handleRestorePlayer(player.id)}
                                disabled={restoring === `player-${player.id}`}
                              >
                                {restoring === `player-${player.id}`
                                  ? "Restoring..."
                                  : "↶ Restore"}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Deleted BragCards List */}
                    {(deletedItems.bragCards?.length || 0) > 0 && (
                      <div className="deleted-items-preview">
                        <h4 className="preview-heading">
                          BragCards ({deletedItems.bragCards.length})
                        </h4>
                        <ul className="deleted-items-list">
                          {deletedItems.bragCards.map((card) => (
                            <li key={card.id} className="deleted-item">
                              <div className="item-info">
                                <span className="item-name">
                                  {card.playerName}
                                </span>
                                {card.milestoneLabel && (
                                  <span className="item-meta">
                                    {card.milestoneLabel}
                                  </span>
                                )}
                              </div>
                              <button
                                className="btn-restore-inline"
                                onClick={() => handleRestoreBragCard(card.id)}
                                disabled={restoring === `bragcard-${card.id}`}
                              >
                                {restoring === `bragcard-${card.id}`
                                  ? "Restoring..."
                                  : "↶ Restore"}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <button
                      type="button"
                      className="btn-view-all-deleted"
                      onClick={() => navigate("/recently-deleted")}
                    >
                      View All Deleted Items →
                    </button>
                  </div>
                ) : (
                  <div className="no-deleted-items">
                    <span className="empty-icon">✨</span>
                    <p>No recently deleted items</p>
                  </div>
                )}
              </div>
            </div>

            {/* Danger Zone */}
            <div
              className="form-section"
              style={{
                borderTop: "2px solid #c0001a",
                paddingTop: 24,
                marginTop: 8,
              }}
            >
              <h2 className="form-section-title" style={{ color: "#c0001a" }}>
                Danger Zone
              </h2>
              <p style={{ fontSize: 14, color: "#4a5568", marginBottom: 16 }}>
                Permanently delete your account and all associated data. This
                action cannot be undone.
              </p>
              <button
                type="button"
                onClick={() => setShowDeleteModal(true)}
                style={{
                  background: "none",
                  border: "1.5px solid #c0001a",
                  color: "#c0001a",
                  borderRadius: 8,
                  padding: "10px 20px",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Delete My Account
              </button>
            </div>

            {/* Save Button */}
            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? "Saving Changes..." : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Toast Notifications */}
      {message.text && message.type === "success" && (
        <div className="toast-message toast-success">{message.text}</div>
      )}
      {message.text && message.type === "error" && (
        <div className="toast-message toast-error">{message.text}</div>
      )}

      {showDeleteModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "0 16px",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              padding: "28px 24px",
              maxWidth: 460,
              width: "100%",
              boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            }}
          >
            <h2 style={{ color: "#c0001a", marginBottom: 12, fontSize: 20 }}>
              Delete Account
            </h2>
            <p
              style={{
                fontSize: 14,
                color: "#1a1e2e",
                marginBottom: 16,
                lineHeight: 1.6,
              }}
            >
              This will cancel your subscription and schedule your account for
              permanent deletion in 30 days. All your players, games, BragCards,
              and graphics will be deleted.
            </p>
            <div
              style={{
                background: "#fff5f5",
                border: "1px solid #c0001a",
                borderRadius: 8,
                padding: "12px 14px",
                marginBottom: 20,
                fontSize: 13,
                color: "#c0001a",
                lineHeight: 1.5,
              }}
            >
              <strong>No refunds are issued</strong> for unused subscription
              time per our Terms of Service.
            </div>
            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                marginBottom: 20,
                cursor: "pointer",
                fontSize: 14,
                color: "#1a1e2e",
              }}
            >
              <input
                type="checkbox"
                checked={deleteAcknowledged}
                onChange={(e) => setDeleteAcknowledged(e.target.checked)}
                style={{ marginTop: 2, flexShrink: 0 }}
              />
              I understand this is permanent and no refund will be issued.
            </label>
            <div style={{ marginBottom: 20 }}>
              <label
                style={{
                  fontSize: 13,
                  color: "#4a5568",
                  display: "block",
                  marginBottom: 6,
                }}
              >
                Type <strong>DELETE</strong> to confirm:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1.5px solid #d0d5e0",
                  fontSize: 14,
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText("");
                  setDeleteAcknowledged(false);
                }}
                style={{
                  flex: 1,
                  padding: "10px",
                  borderRadius: 8,
                  border: "1.5px solid #d0d5e0",
                  background: "#fff",
                  color: "#4a5568",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={
                  deleteConfirmText !== "DELETE" ||
                  !deleteAcknowledged ||
                  deleting
                }
                style={{
                  flex: 1,
                  padding: "10px",
                  borderRadius: 8,
                  border: "none",
                  background:
                    deleteConfirmText === "DELETE" && deleteAcknowledged
                      ? "#c0001a"
                      : "#e2e8f0",
                  color:
                    deleteConfirmText === "DELETE" && deleteAcknowledged
                      ? "#fff"
                      : "#a0aec0",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor:
                    deleteConfirmText === "DELETE" && deleteAcknowledged
                      ? "pointer"
                      : "not-allowed",
                }}
              >
                {deleting ? "Deleting..." : "Delete My Account"}
              </button>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </>
  );
}

export default AccountSettings;
